
import { call, select, takeLatest } from 'redux-saga/effects';
import * as api from '@api/ClinicalTrialContext';
import { actions, initialState } from './clinicalTrialContext.slice';
import {
  ClinicalTrialContextFetchAction,
  ClinicalTrialContextFetchSuccessAction,
  ClinicalTrialContextState,
  Label,
} from './clinicalTrialContext.types';
import { Resp, dispatch } from '../helpers';
import {
  makeAffiliatedSiteList,
  makeFlatAllLabels,
  makeFlatVisits,
} from './transformers';
import { groupBy } from 'lodash';
import { UploadFileTypeEnum } from '@dynamika-shared/types/inventory';
import {
  actions as readerCacheActions,
  selectCachedCtContext,
  selectIsReaderCacheActive,
} from '../readerCache/readerCache.slice';

export const clinicalTrialContextStorageKey = 'clinicalTrialContextStorage';


export function* fetchClinicalTrialContextWorker(
  action: ClinicalTrialContextFetchAction
) {
  const clinicalTrialId = action.payload;

  // readerCache is injected only in the reader role; in other roles (admin/pm,
  // QC) the slice is absent — the safe selectors below treat that as a miss and
  // skip caching entirely.
  const cached: ClinicalTrialContextFetchSuccessAction['payload'] | undefined =
    yield select(selectCachedCtContext, clinicalTrialId);

  if (cached) {
    yield dispatch(actions.fetchClinicalTrialContextSuccess(cached));
    return cached;
  }

  try {
    const response: Resp<typeof api.fetchClinicalTrialContext> = yield call(
      api.fetchClinicalTrialContext,
      action.payload
    );

    const affiliatedSiteList: ReturnType<typeof makeAffiliatedSiteList> =
      yield call(
        makeAffiliatedSiteList,
        response.configuration.sites,
        response?.affiliations
      );

    const flatAllLabels: ReturnType<typeof makeFlatAllLabels> = yield call(
      makeFlatAllLabels,
      response.configuration.labelGroups
    );

    const flatVisits: ReturnType<typeof makeFlatVisits> = yield call(
      makeFlatVisits,
      response.configuration.cohorts,
      flatAllLabels
    );

    const groupedLabels = groupBy(flatAllLabels, 'dataType') as Record<
      UploadFileTypeEnum,
      Label[]
    >;

    const result = {
      data: { ...response, affiliatedSiteList },
      flatAllLabels,
      flatVisits,
      groupedLabels,
    };

    yield call(
      sessionStorage.setItem.bind(sessionStorage),
      clinicalTrialContextStorageKey,
      JSON.stringify(result)
    );
    const isReaderCacheActive: boolean = yield select(selectIsReaderCacheActive);
    if (isReaderCacheActive) {
      yield dispatch(
        readerCacheActions.setCtContext({ clinicalTrialId, data: result })
      );
    }
    yield dispatch(actions.fetchClinicalTrialContextSuccess(result));
    return result;
  } catch (error) {
    yield dispatch(actions.fetchClinicalTrialContextFailure(error as Error));
  }
}

export function* restoreContextWorker() {
  try {
    const stateJSON: string | null = yield call(
      sessionStorage.getItem.bind(sessionStorage),
      clinicalTrialContextStorageKey
    );

    if (!stateJSON) {
      return;
    }

    const state: ClinicalTrialContextState = yield call(
      JSON.parse.bind(JSON),
      stateJSON
    );

    if (state) {
      yield dispatch(actions.fetchClinicalTrialContextRestore(state));
    }
  } catch (error) {
    yield call(
      console.log.bind(console),
      'Error restoring clinical trial context',
      error
    );
  }
}

export function* clearDataWorker() {
  yield call(
    sessionStorage.setItem.bind(sessionStorage),
    clinicalTrialContextStorageKey,
    JSON.stringify(initialState)
  );
}

export function* clinicalTrialContextSaga() {
  yield takeLatest(
    actions.fetchClinicalTrialContext.type,
    fetchClinicalTrialContextWorker
  );
  yield takeLatest(actions.clearData.type, clearDataWorker);
  yield takeLatest('@@INIT', restoreContextWorker);
}
